import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Palette, Image as ImageIcon, Type, Check, ArrowRight, ArrowLeft, Sparkles, Rocket, Upload, Menu } from "lucide-react";

/**
 * Tenant Onboarding Wizard.
 *
 * A three-step "sub-60-second" form for a Founder on a fresh install:
 *   1) Brand identity — name + wordmark + tagline
 *   2) Colour + logo URL
 *   3) Hero background — pick from the curated Unsplash gallery
 *
 * On save we PUT `/api/brand/config` and bounce to /dashboard. The
 * wizard is idempotent — the Founder can revisit `/onboarding` any
 * time to re-run it. Non-Founders get bounced away.
 */
const PRESET_COLORS = [
  { hex: "#1B7A3E", dark: "#0b3d1f", label: "Emerald" },
  { hex: "#0E6BA8", dark: "#053b6b", label: "Ocean" },
  { hex: "#D97706", dark: "#7c3d05", label: "Sunset" },
  { hex: "#B91C1C", dark: "#7f1010", label: "Ruby" },
  { hex: "#7C3AED", dark: "#3f1d99", label: "Amethyst" },
  { hex: "#0F766E", dark: "#083f3b", label: "Teal" },
  { hex: "#111827", dark: "#000000", label: "Charcoal" },
  { hex: "#DB2777", dark: "#831646", label: "Rose" },
];

export default function Onboarding() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [brand, setBrand] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    if (user.role !== "FOUNDER") { nav("/dashboard"); return; }
    // Fetch the current brand + curated hero-photo gallery in parallel.
    Promise.all([
      api.get("/brand/config").then(r => r.data).catch(() => ({})),
      api.get("/brand/hero-photos").then(r => r.data.photos || []).catch(() => []),
    ]).then(([b, p]) => { setBrand(b); setPhotos(p); });
  }, [authLoading, user, nav]);

  const set = (k, v) => setBrand(b => ({ ...b, [k]: v }));

  // Convert a File → data-URL (base64) client-side so the logo is
  // instantly usable as `brand.logo_url` without any object-storage
  // round-trip. Logos are small (usually <100 KB PNGs) — we cap at
  // 500 KB to keep the brand_config doc reasonable and stay well
  // under Mongo's 16 MB doc limit even after the whole config is
  // JSON-encoded. Larger images should paste a hosted URL instead.
  const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const acceptLogoFile = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) return toast.error("Please pick an image file (PNG, JPG, SVG)");
    if (file.size > 500 * 1024) return toast.error("Logo too large — under 500 KB please (paste a hosted URL for anything larger)");
    setUploadingLogo(true);
    try {
      const dataUrl = await readAsDataUrl(file);
      set("logo_url", dataUrl);
      toast.success("Logo added — see the live preview below");
    } catch {
      toast.error("Could not read the file");
    } finally { setUploadingLogo(false); }
  };
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) acceptLogoFile(file);
  };

  const finish = async () => {
    setSaving(true);
    try {
      await api.put("/brand/config", brand);
      toast.success("You're all set — welcome aboard!");
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (authLoading || !brand) {
    return <div className="min-h-screen flex items-center justify-center text-[color:var(--aii-muted)]">Loading…</div>;
  }

  const canNext =
    (step === 1 && (brand.name || "").trim().length > 0) ||
    (step === 2) ||
    (step === 3);

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-900">
      {/* Same travel-photo background family as /login — feels
          continuous when the Founder lands here. */}
      <img
        src={brand.hero_bg_url || "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=2000&q=70"}
        alt="" aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"/>
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-slate-900/80 to-slate-950/90"/>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="onboarding-wizard">
          {/* Header with step indicator */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-brand">
              <Sparkles size={12}/> Tenant setup — Step {step} of 3
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-1 text-slate-900">
              {step === 1 && "Name your workspace"}
              {step === 2 && "Pick your colour + logo"}
              {step === 3 && "Choose a hero photo"}
            </h1>
            <div className="mt-3 flex gap-1.5">
              {[1, 2, 3].map(n => (
                <div key={n} className={`h-1.5 flex-1 rounded-full ${n <= step ? "bg-brand" : "bg-slate-200"}`}/>
              ))}
            </div>
          </div>

          <div className="p-6 space-y-4 min-h-[280px]">
            {step === 1 && (
              <>
                <div className="text-xs text-[color:var(--aii-muted)] flex items-center gap-1"><Type size={12}/> This is what participants + partners see on the login page.</div>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[color:var(--aii-muted)]">Brand name *</span>
                  <input
                    data-testid="onb-name"
                    value={brand.name || ""} onChange={e => set("name", e.target.value)}
                    autoFocus placeholder="Global Adventures Group"
                    className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-md text-base"/>
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[color:var(--aii-muted)]">Wordmark (all caps in the header)</span>
                  <input
                    data-testid="onb-wordmark"
                    value={brand.wordmark || ""} onChange={e => set("wordmark", e.target.value)}
                    placeholder="GLOBAL ADVENTURES"
                    className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-md text-base tracking-wider"/>
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[color:var(--aii-muted)]">Tagline</span>
                  <input
                    data-testid="onb-tagline"
                    value={brand.tagline || ""} onChange={e => set("tagline", e.target.value)}
                    placeholder="Meaningful adventures, delivered."
                    className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-md text-base"/>
                </label>
              </>
            )}

            {step === 2 && (
              <>
                <div className="text-xs text-[color:var(--aii-muted)] flex items-center gap-1"><Palette size={12}/> Colour drives buttons, headings, and accents everywhere.</div>
                <div>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[color:var(--aii-muted)]">Primary colour</span>
                  <div className="mt-2 grid grid-cols-4 sm:grid-cols-8 gap-2" data-testid="onb-colors">
                    {PRESET_COLORS.map(c => {
                      const active = (brand.primary_color || "").toLowerCase() === c.hex.toLowerCase();
                      return (
                        <button key={c.hex}
                                data-testid={`onb-color-${c.label.toLowerCase()}`}
                                type="button"
                                onClick={() => { set("primary_color", c.hex); set("primary_dark", c.dark); }}
                                title={c.label}
                                className={`h-11 rounded-md relative border-2 ${active ? "border-slate-900 ring-2 ring-slate-900/20" : "border-transparent"}`}
                                style={{ backgroundColor: c.hex }}>
                          {active && <Check size={14} className="text-white absolute inset-0 m-auto drop-shadow"/>}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[color:var(--aii-muted)] flex items-center gap-1"><ImageIcon size={11}/> Logo (optional)</span>
                  {/* Drag-drop zone — reads the dropped file into a
                      data-URL client-side. No round-trip needed. */}
                  <div
                    data-testid="onb-logo-drop"
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`mt-1 rounded-md border-2 border-dashed p-4 text-center cursor-pointer transition
                      ${dragOver ? "border-brand bg-emerald-50" : "border-slate-300 hover:border-brand hover:bg-slate-50"}`}>
                    <Upload size={18} className="mx-auto text-slate-400"/>
                    <div className="text-xs mt-1 text-slate-600">
                      {uploadingLogo ? "Reading…" : "Drop a PNG / JPG / SVG here or click to pick"}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Max 500 KB · anything larger, paste a URL below</div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid="onb-logo-file"
                      onChange={e => acceptLogoFile(e.target.files?.[0])}/>
                  </div>
                  <input
                    data-testid="onb-logo"
                    value={brand.logo_url && brand.logo_url.startsWith("data:") ? "" : brand.logo_url || ""}
                    onChange={e => set("logo_url", e.target.value)}
                    placeholder="…or paste a hosted URL"
                    className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"/>
                </label>
                {brand.logo_url && (
                  <div className="flex items-center gap-3 p-3 rounded-md bg-slate-50 border border-slate-200">
                    <img src={brand.logo_url} alt="logo preview" className="w-12 h-12 object-contain rounded"
                         onError={e => e.currentTarget.style.display = 'none'}/>
                    <span className="text-xs text-slate-600">Live preview — this is how your logo will look in the sidebar.</span>
                  </div>
                )}

                {/* Sidebar-shape live preview — mimics Layout.jsx so
                    the Founder sees exactly what wordmark + colour
                    combination will land on the app after saving. */}
                <div className="rounded-md border border-slate-200 overflow-hidden" data-testid="onb-sidebar-preview">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 px-3 pt-2">Live sidebar preview</div>
                  <div className="flex items-stretch min-h-[80px]">
                    <div className="p-3 border-r border-slate-200 flex flex-col gap-2 bg-white flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {brand.logo_url ? (
                          <img src={brand.logo_url} alt="" className="w-9 h-9 object-contain shrink-0"/>
                        ) : (
                          <div className="w-9 h-9 rounded-md flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: brand.primary_color || "#1B7A3E" }}>
                            {(brand.wordmark || brand.name || "T").trim().charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-display font-bold text-sm truncate" style={{ color: brand.primary_dark || "#0b3d1f" }}>
                            {(brand.wordmark || brand.name || "TRAVEL ERP")}
                            <span style={{ color: brand.primary_color || "#1B7A3E" }}>®</span>
                          </div>
                          <div className="text-[10px] text-slate-500 truncate">{brand.tagline || "Your tagline appears here"}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="px-2 py-1 text-[10px] rounded font-semibold text-white" style={{ backgroundColor: brand.primary_color || "#1B7A3E" }}>Dashboard</div>
                        <Menu size={12} className="text-slate-400"/>
                        <span className="text-[10px] text-slate-400">nav preview…</span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="text-xs text-[color:var(--aii-muted)]">Pick a travel photo for the login page — tap any tile.</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="onb-hero-grid">
                  {photos.map(p => {
                    const active = brand.hero_bg_url === p.full;
                    return (
                      <button key={p.id}
                              data-testid={`onb-hero-${p.id}`}
                              type="button"
                              onClick={() => set("hero_bg_url", p.full)}
                              className={`relative aspect-video rounded-lg overflow-hidden border-2 ${active ? "border-brand ring-2 ring-brand/30" : "border-transparent hover:border-slate-300"}`}
                              title={p.label}>
                        <img src={p.thumb} alt={p.label} className="w-full h-full object-cover" loading="lazy"/>
                        {active && (
                          <div className="absolute inset-0 bg-brand/40 flex items-center justify-center">
                            <div className="bg-white rounded-full p-1"><Check size={16} className="text-brand"/></div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="p-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
            <button
              type="button"
              onClick={() => step > 1 ? setStep(s => s - 1) : nav("/dashboard")}
              disabled={saving}
              data-testid="onb-back"
              className="text-sm px-4 py-2 rounded-md hover:bg-slate-200 text-slate-700 flex items-center gap-1 disabled:opacity-50">
              <ArrowLeft size={13}/> {step === 1 ? "Skip for now" : "Back"}
            </button>
            {step < 3 ? (
              <button
                type="button"
                onClick={() => setStep(s => s + 1)}
                disabled={!canNext}
                data-testid="onb-next"
                className="text-sm px-5 py-2 bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1">
                Continue <ArrowRight size={13}/>
              </button>
            ) : (
              <button
                type="button"
                onClick={finish}
                disabled={saving}
                data-testid="onb-finish"
                className="text-sm px-5 py-2 bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1">
                <Rocket size={13}/> {saving ? "Setting up…" : "Launch workspace"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
